import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { leadKeys, contactKeys, dealKeys } from '@/lib/query-keys';
import { leadService } from '@/services/lead.service';
import { useLeadFilterStore } from '@/stores/leadFilterStore';

export function useLeads() {
  const queryClient = useQueryClient();
  const filters = useLeadFilterStore();

  const leadsQuery = useQuery({
    queryKey: leadKeys.list(filters.current),
    queryFn: () => leadService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createLead = useMutation({
    mutationFn: (data: Parameters<typeof leadService.create>[0]) => leadService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Lead created successfully');
    },
    onError: () => toast.error('Failed to create lead'),
  });

  const updateLead = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof leadService.update>[1] }) =>
      leadService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
      toast.success('Lead updated successfully');
    },
    onError: () => toast.error('Failed to update lead'),
  });

  const deleteLead = useMutation({
    mutationFn: ({ id, deleteDeal }: { id: string; deleteDeal?: boolean }) => leadService.delete(id, { deleteDeal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      toast.success('Lead deleted successfully');
    },
    onError: () => toast.error('Failed to delete lead'),
  });

  const convertLead = useMutation({
    mutationFn: ({ id, data }: { id: string; data?: Record<string, unknown> }) =>
      leadService.convert(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
      toast.success('Lead converted successfully');
    },
    onError: () => toast.error('Failed to convert lead'),
  });

  const bulkAssign = useMutation({
    mutationFn: (data: { ids: string[]; assignedToId: string }) => leadService.bulkAssign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Leads assigned successfully');
    },
    onError: () => toast.error('Failed to assign leads'),
  });

  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => leadService.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Leads deleted successfully');
    },
    onError: () => toast.error('Failed to delete leads'),
  });

  return {
    leads: leadsQuery.data?.data ?? [],
    meta: leadsQuery.data?.meta,
    isLoading: leadsQuery.isLoading,
    filters,
    createLead,
    updateLead,
    deleteLead,
    convertLead,
    bulkAssign,
    bulkDelete,
    exportCsv: () => leadService.exportCsv(filters.current),
  };
}

export function useLead(id: string) {
  const queryClient = useQueryClient();

  const leadQuery = useQuery({
    queryKey: leadKeys.detail(id),
    queryFn: () => leadService.getById(id),
    enabled: !!id,
  });

  const activitiesQuery = useQuery({
    queryKey: leadKeys.activities(id),
    queryFn: () => leadService.getActivities(id),
    enabled: !!id,
  });

  const notesQuery = useQuery({
    queryKey: leadKeys.notes(id),
    queryFn: () => leadService.getNotes(id),
    enabled: !!id,
  });

  const tasksQuery = useQuery({
    queryKey: leadKeys.tasks(id),
    queryFn: () => leadService.getTasks(id),
    enabled: !!id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
  };

  return {
    lead: leadQuery.data?.data,
    isLoading: leadQuery.isLoading,
    activities: activitiesQuery.data?.data ?? [],
    notes: notesQuery.data?.data ?? [],
    tasks: tasksQuery.data?.data ?? [],
    invalidate,
  };
}
